import React from "react";
import { useAuth } from "./AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { useTheme } from "../hooks/useTheme";
import { authSearch } from "./redirect";

export default function ProtectedRoute({ children, requiredRole = null }) {
  const { currentUser, loading, isAuthReady, hasRole } = useAuth();
  useTheme(); // applies the persisted light/dark class to <html>
  const navigate = useNavigate();
  const location = useLocation();

  // Helper to get language from URL
  const getLangFromUrl = () => {
    const params = new URLSearchParams(location.search);
    return params.get("lang") === "zh" ? "zh" : "en";
  };

  const language = getLangFromUrl();

  // Normalise requiredRole to an array; null/undefined → no restriction.
  // Passes if the user has AT LEAST ONE of the required roles.
  const requiredRoles = requiredRole
    ? Array.isArray(requiredRole)
      ? requiredRole
      : [requiredRole]
    : [];

  const hasRequiredRole =
    requiredRoles.length === 0 || requiredRoles.some((r) => hasRole(r));

  // `loading` only covers the first auth check on page load. A sign-in after
  // that - the login page handing back a deep link such as the newsletter's -
  // sets currentUser at once but fetches roles afterwards, and isAuthReady is
  // what stays false until they land. Without it the role check runs against
  // an empty list and bounces a user who does have the role.
  const authPending = loading || !isAuthReady;

  // The page that was asked for, so the auth pages can hand it back once the
  // user is through. A plain string rather than the location object, because
  // this feeds the effect's dependency list and a fresh object identity on
  // every render would re-run it constantly.
  const from = `${location.pathname}${location.search}`;

  useEffect(() => {
    if (authPending) return;

    // replace, not push: these are bounces, and leaving them in history
    // means the browser's back button lands on a page that immediately
    // bounces the user forward again.
    if (!currentUser) {
      navigate(`/login${authSearch(language, from)}`, { replace: true });
    } else if (!currentUser.emailVerified) {
      navigate(`/verify-email${authSearch(language, from)}`, {
        replace: true,
      });
    } else if (!hasRequiredRole) {
      // Not remembered on purpose - the destination is not one this
      // account may open, so holding onto it would only replay the denial.
      navigate(`/tg-product-assistant?lang=${language}&error=access_denied`, {
        replace: true,
      });
    }
  }, [currentUser, navigate, language, hasRequiredRole, authPending, from]);

  if (
    authPending ||
    !currentUser ||
    !currentUser.emailVerified ||
    !hasRequiredRole
  ) {
    return (
      <div className="flex h-screen bg-slate-100 dark:bg-dashboard-bg text-black font-sans">
        <div className="flex-1 flex flex-col gap-8 items-center justify-center -mt-36">
          <div className="flex flex-col gap-6 items-center text-center">
            {/* Enhanced Loading Text */}
            <div className="space-y-3">
              <h2 className="text-2xl font-bold text-black dark:text-white">
                {language === "zh" ? "正在加載管理面板" : "Loading Panel"}
              </h2>
              <div className="flex items-center gap-3 justify-center">
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-orange-500 rounded-full animate-bounce"></div>
                  <div
                    className="w-2 h-2 bg-orange-500 rounded-full animate-bounce"
                    style={{ animationDelay: "0.1s" }}
                  ></div>
                  <div
                    className="w-2 h-2 bg-orange-500 rounded-full animate-bounce"
                    style={{ animationDelay: "0.2s" }}
                  ></div>
                </div>
                <span className="text-orange-600 dark:text-orange-300 text-lg font-medium">
                  {language === "zh" ? "正在準備數據..." : "Preparing data..."}
                </span>
              </div>
            </div>

            {/* Enhanced Description */}
            <div className="bg-black/30 dark:bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 max-w-md">
              <p className="text-slate-100 text-sm leading-relaxed">
                {language === "zh"
                  ? "請稍候，我們正在為您加載數據。這可能需要幾秒鐘的時間。"
                  : "Please wait while we load data. This may take a few seconds."}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return children;
}
